import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { DomainContext } from '@/shared/contexts/DomainProvider';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Z_INDEX } from '@/lib/z-index';

interface LogoWithDomainSwitcherProps {
  isCollapsed?: boolean;
}

/**
 * LogoWithDomainSwitcher Component
 *
 * Integrated logo and domain switcher for sidebar navigation.
 * Displays "Thrive {{Domain}}" where domain is clickable with:
 * - Underline animation effect
 * - Dropdown arrow
 * - Domain switching functionality
 *
 * Uses URL as single source of truth via DomainProvider.
 */
export const LogoWithDomainSwitcher: React.FC<LogoWithDomainSwitcherProps> = ({ isCollapsed = false }) => {
  const domainContext = useContext(DomainContext);
  
  // Graceful fallback if context is temporarily unavailable
  if (!domainContext) {
    return (
      <div className={cn(
        "py-6 border-b border-gray-200",
        isCollapsed ? "px-4 flex justify-center" : "px-8"
      )}>
        <div className={cn(
          "flex items-center",
          isCollapsed ? "" : "space-x-2"
        )}>
          <Link to="/home">
            <img src="/thrive_icon.png" alt="Thrive" className="w-8 h-8" />
          </Link>
          {!isCollapsed && <h1 className="text-lg font-semibold text-gray-900">Thrive</h1>}
        </div>
      </div>
    );
  }
  
  const { currentDomain, domainConfig, availableDomains, switchToDomain } = domainContext;
  const [isDomainDropdownOpen, setIsDomainDropdownOpen] = useState(false);
  const domainDropdownRef = useRef<HTMLDivElement>(null);

  const handleDomainDropdownToggle = () => {
    setIsDomainDropdownOpen(!isDomainDropdownOpen);
  };

  const handleDomainSwitch = (domainId: string) => {
    if (domainId !== currentDomain) {
      switchToDomain(domainId);
    }
    setIsDomainDropdownOpen(false);
  };

  // Handle click outside to close domain dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(event.target as Node)) {
        setIsDomainDropdownOpen(false);
      }
    };

    if (isDomainDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDomainDropdownOpen]);

  // Get platform title components
  const getPlatformTitleParts = () => {
    if (!domainConfig?.id) return { base: 'Thrive', domain: null };
    
    const domainId = domainConfig.id;
    
    switch (domainId) {
      case 'intel':
        return { base: 'Thrive', domain: 'Intel' };
      case 'workflow':
        return { base: 'Thrive', domain: 'Workflow' };
      case 'billing':
        return { base: 'Thrive', domain: 'Billing' };
      case 'hiring':
        return { base: 'Thrive', domain: 'Hiring' };
      case 'sales':
        return { base: 'Thrive', domain: 'Sales' };
      case 'admin':
        return { base: 'Thrive', domain: 'Admin' };
      case 'goals':
        return { base: 'Thrive', domain: 'Goals' };
      case 'crm':
        return { base: 'Thrive', domain: 'CRM' };
      case 'tools':
        return { base: 'Thrive', domain: 'Tools' };
      case 'creative':
        return { base: 'Thrive', domain: 'Creative' };
      default:
        return { base: 'Thrive', domain: null };
    }
  };

  const getDomainDisplayName = (domainId: string) => {
    switch (domainId) {
      case 'intel':
        return 'Intel';
      case 'workflow':
        return 'Workflow';
      case 'billing':
        return 'Billing';
      case 'hiring':
        return 'Hiring';
      case 'sales':
        return 'Sales';
      case 'admin':
        return 'Admin';
      case 'goals':
        return 'Goals';
      case 'crm':
        return 'CRM';
      case 'tools':
        return 'Tools';
      case 'creative':
        return 'Creative';
      default:
        return domainId;
    }
  };

  const getDomainDescription = (domainId: string) => {
    switch (domainId) {
      case 'intel':
        return 'Business intelligence and insights';
      case 'workflow':
        return 'Team capacity and planning';
      case 'billing':
        return 'Monthly billing process';
      case 'hiring':
        return 'Talent acquisition hub';
      case 'sales':
        return 'Business development hub';
      case 'admin':
        return 'System configuration and settings';
      case 'goals':
        return 'Critical Success Factors and Initiatives';
      case 'crm':
        return 'Client relationships and CRM';
      case 'tools':
        return 'Tools and utilities';
      case 'creative':
        return 'Creative department hub';
      default:
        return '';
    }
  };

  const titleParts = getPlatformTitleParts();

  // Collapsed view - only show logo
  if (isCollapsed) {
    return (
      <div className="px-4 py-6 border-b border-gray-200 flex justify-center">
        <Link to="/home">
          <img src="/thrive_icon.png" alt="Thrive" className="w-8 h-8" title={`${titleParts.base} ${titleParts.domain || ''}`} />
        </Link>
      </div>
    );
  }

  return (
    <div className="px-8 py-6 border-b border-gray-200">
      <div className="flex items-center space-x-2">
        <Link to="/home">
          <img src="/thrive_icon.png" alt="Thrive" className="w-8 h-8" />
        </Link>

        {/* Integrated Domain Switcher Title */}
        <div className="relative flex-1" ref={domainDropdownRef}>
          <button
            onClick={handleDomainDropdownToggle}
            className={cn(
              "flex items-center space-x-2 group transition-all duration-200",
              "hover:text-gray-700 focus:outline-none focus:text-gray-700"
            )}
            aria-expanded={isDomainDropdownOpen}
            aria-haspopup="listbox"
            aria-label={`Current domain: ${titleParts.base} ${titleParts.domain || ''}. Click to switch domains.`}
          >
            <h1 className="text-lg font-semibold text-gray-900 flex items-baseline">
              <span>{titleParts.base}</span>
              {titleParts.domain && (
                <>
                  <span className="mx-0.5"></span>
                  <span className="relative">
                    {titleParts.domain}
                    <span className={cn(
                      "absolute bottom-0 left-0 w-full h-0.5 bg-[#F15A25] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left",
                      isDomainDropdownOpen && "scale-x-100"
                    )} />
                  </span>
                </>
              )}
            </h1>
            
            <ChevronDown
              className={cn(
                "h-4 w-4 text-[#F15A25] transition-transform duration-200",
                isDomainDropdownOpen && "rotate-180"
              )}
            />
          </button>

          {/* Domain Dropdown - positioned to start at logo edge */}
          {isDomainDropdownOpen && availableDomains.length > 0 && (() => {
            const buttonRect = domainDropdownRef.current?.getBoundingClientRect();
            return (
              <div
                className={cn(
                  "fixed w-80 bg-white rounded-2xl shadow-xl ring-1 ring-purple-200",
                  "transform transition-all duration-200 ease-out origin-top-left",
                  "animate-in slide-in-from-top-2 fade-in-0"
                )}
                style={{
                  zIndex: Z_INDEX.FLOATING_DROPDOWNS,
                  left: buttonRect ? `${buttonRect.left - 76}px` : '0px',
                  top: buttonRect ? `${buttonRect.bottom - 2}px` : '0px'
                }}
                role="listbox"
                aria-label="Available domains"
              >
                <div className="py-1">
                  {availableDomains.map((domain) => {
                    const isActive = domain.id === currentDomain;
                    
                    return (
                      <button
                        key={domain.id}
                        onClick={() => handleDomainSwitch(domain.id)}
                        className={cn(
                          "w-full text-left px-4 py-3 flex items-center justify-between gap-3",
                          "hover:bg-[#ebe4f1]/50 focus:bg-[#ebe4f1]/50 focus:outline-none",
                          "transition-colors duration-150",
                          isActive && "bg-[#ebe4f1] text-[#311c3b]"
                        )}
                        role="option"
                        aria-selected={isActive}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900">
                            {getDomainDisplayName(domain.id)}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 truncate">
                            {getDomainDescription(domain.id)}
                          </div>
                        </div>
                        {isActive && (
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white flex-shrink-0">
                            <Check className="h-4 w-4 text-[#5b3d72]" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

